import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { db } from "../firebase"; // 👈 Path check kar lijiyega
import { 
  collection, 
  onSnapshot, 
  doc, 
  setDoc, 
  updateDoc 
} from "firebase/firestore";

const AppContext = createContext(null);

const STORAGE_KEY = "mrlappy_state_v1";

const BASE_DIAGNOSTIC_RATE = 30;
const INSPECTION_FEE = 150;

// Random 4 digit generator
const rand4 = () => String(Math.floor(1000 + Math.random() * 9000));

const loadSession = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed.session || null;
  } catch {
    return null;
  }
};

const saveSession = (sessionData) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ session: sessionData }));
  } catch {
    /* no-op */
  }
};

export function AppProvider({ children }) {
  const initialSession = loadSession();

  const [bookings, setBookings] = useState([]);
  const [session, setSession] = useState(initialSession);

  const [route, setRoute] = useState(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (params.get("access") === "mrlappyadmin") return "admin-gateway";
    }
    if (initialSession?.role === "admin") return "admin-dashboard";
    if (initialSession?.role === "customer") return "customer-hub";
    return "landing";
  });

  // 🚀 CLEAN REAL-TIME LISTENER: No extra seeding loop
  useEffect(() => {
    const bookingsCollection = collection(db, "bookings");
    
    const unsubscribe = onSnapshot(bookingsCollection, (snapshot) => {
      const list = snapshot.docs.map(docSnap => ({
        ...docSnap.data()
      }));
      // Sort: Naya booking sabse upar dikhega
      list.sort((a, b) => b.createdAt - a.createdAt);
      setBookings(list);
    }, (error) => {
      console.error("Firestore sync error:", error);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    saveSession(session);
  }, [session]);

  // ➕ Clean Add Booking
  const addBooking = useCallback(
    async ({ brand, model, issue, customIssue, address, description }) => {
      const id = `MLY-${Math.floor(4100 + Math.random() * 899)}`;
      const gateOTP = rand4(); // 👈 dynamic random generation
      const handoverToken = rand4();
      
      const newBooking = {
        id,
        phone: session?.phone || "",
        brand,
        model,
        issue,
        customIssue,
        address,
        description,
        status: "Assigned",
        gateOTP, 
        handoverToken,
        extras: [],
        baseDiagnostic: BASE_DIAGNOSTIC_RATE,
        inspectionFee: INSPECTION_FEE,
        isPaid: false,
        createdAt: Date.now(),
        completedAt: null,
      };

      try {
        // Sirf firestore mein set karenge, UI automatic onSnapshot se sync ho jayegi
        await setDoc(doc(db, "bookings", id), newBooking);
        return newBooking;
      } catch (err) {
        console.error("Firestore Save Error: ", err);
        return null;
      }
    },
    [session]
  );

  const verifyGateOTP = useCallback(async (bookingId, code) => {
    const target = bookings.find((x) => x.id === bookingId);
    if (target && target.gateOTP === code && target.status === "Assigned") {
      try {
        const docRef = doc(db, "bookings", bookingId);
        await updateDoc(docRef, { status: "In-Progress" });
        return true;
      } catch (err) {
        console.error(err);
        return false;
      }
    }
    return false;
  }, [bookings]);

  const addExtraToBooking = useCallback(async (bookingId, name, cost) => {
    const target = bookings.find((x) => x.id === bookingId);
    if (!target) return;
    
    const currentExtras = Array.isArray(target.extras) ? target.extras : [];
    const item = { id: Date.now(), name, cost: Number(cost) || 0 };
    
    try {
      const docRef = doc(db, "bookings", bookingId);
      await updateDoc(docRef, { extras: [...currentExtras, item] });
    } catch (err) {
      console.error(err);
    }
  }, [bookings]);

  const removeExtraFromBooking = useCallback(async (bookingId, itemId) => {
    const target = bookings.find((x) => x.id === bookingId);
    if (!target) return;
    
    const currentExtras = Array.isArray(target.extras) ? target.extras : [];
    const updatedExtras = currentExtras.filter((i) => i.id !== itemId);
    
    try {
      const docRef = doc(db, "bookings", bookingId);
      await updateDoc(docRef, { extras: updatedExtras });
    } catch (err) {
      console.error(err);
    }
  }, [bookings]);

  const closeBookingWithToken = useCallback(async (bookingId, token) => {
    const target = bookings.find((x) => x.id === bookingId);
    if (target && target.handoverToken === token && target.status !== "Completed") {
      try {
        const docRef = doc(db, "bookings", bookingId);
        await updateDoc(docRef, { 
          status: "Completed", 
          isPaid: true, 
          completedAt: Date.now() 
        });
        return true;
      } catch (err) {
        console.error(err);
        return false;
      }
    }
    return false;
  }, [bookings]);

  const loginCustomer = useCallback((phone) => {
    setSession({ role: "customer", phone });
  }, []);

  const loginAdmin = useCallback(() => {
    setSession({ role: "admin" });
  }, []);

  const logout = useCallback(() => {
    setSession(null);
    setRoute("landing");
    if (typeof window !== "undefined") {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const bookingsForCurrentUser = useMemo(() => {
    if (!session || session.role !== "customer") return [];
    return bookings.filter((b) => b.phone === session.phone);
  }, [bookings, session]);

  const value = {
    bookings,
    session,
    route,
    setRoute,
    addBooking,
    verifyGateOTP,
    addExtraToBooking,
    removeExtraFromBooking,
    closeBookingWithToken,
    loginCustomer,
    loginAdmin,
    logout,
    bookingsForCurrentUser,
    BASE_DIAGNOSTIC_RATE,
    INSPECTION_FEE,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used inside AppProvider");
  return ctx;
}